import { Dispatch, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { buildLink } from "common/routing";
import { MFAFlowAction } from "common-client/utils/useMFAFlow";
import {
  AdminLoginMutation,
  AdminLoginMutationVariables,
  GetUserAccountsQuery,
  UserLoginErrorCode,
  UserLoginMutation,
  UserLoginMutationVariables,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { RequestFormParams } from "../../utils/params";
import { LoginPageProps } from "./LoginPage";
import {
  ProvideCredentialsForm,
  ProvideCredentialsFormData,
} from "./ProvideCredentialsForm";

export type ProvideCredentialsProps = {
  dispatch: Dispatch<MFAFlowAction>;
  error: Maybe<{ message: string; code?: UserLoginErrorCode }>;
  loginMutation: LoginPageProps["loginMutation"];
  onSuccess: () => void;
  showPasswordReset?: boolean;
  title: string;
  accounts: NonNullable<GetUserAccountsQuery["getUserAccountsByEmail"]>;
  currentAccount: Maybe<
    NonNullable<GetUserAccountsQuery["getUserAccountsByEmail"]>[0]
  >;
};

export const ProvideCredentials = ({
  dispatch,
  error,
  loginMutation,
  onSuccess,
  showPasswordReset,
  title,
  accounts,
  currentAccount,
}: ProvideCredentialsProps) => {
  const { t } = useTranslation();
  const { addErrorToast } = useStatusToasts();
  const history = useHistory();
  const { watch } = useFormContext<ProvideCredentialsFormData>();
  const isMountedRef = useRef(true);

  const email = watch("email");

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [login] = useMutation<
    AdminLoginMutation | UserLoginMutation,
    AdminLoginMutationVariables & UserLoginMutationVariables
  >(loginMutation, {
    onCompleted: data => {
      if (data.login.ok) {
        if ("totpEnabled" in data.login && data.login.totpEnabled) {
          const needsTotpSetup = "qrCode" in data.login && data.login.qrCode;
          needsTotpSetup ?
            dispatch({ type: "setupTotp", data: data.login })
          : dispatch({ type: "verifyTotpCode", data: data.login });
        } else {
          onSuccess();
        }
      } else if (
        data.login.error?.code === UserLoginErrorCode.PASSWORD_RESET_REQUIRED
      ) {
        history.push(
          buildLink("passwordReset", {}, {
            resetRequired: "true",
          } satisfies RequestFormParams),
        );
      } else {
        dispatch({ type: "login", data: data.login });
      }
    },
    onError: () => {
      if (isMountedRef.current) {
        addErrorToast(t("sign-in-unexpected-network-error"));
      }
    },
  });

  const handleLogin = async (loginData: ProvideCredentialsFormData) => {
    await login({
      variables: { email: loginData.email!, password: loginData.password! },
    });
  };

  const handleClickPasswordReset =
    showPasswordReset ?
      () => {
        history.push(
          buildLink("passwordReset", {}, {
            email,
          } satisfies RequestFormParams),
        );
      }
    : undefined;

  return (
    <ProvideCredentialsForm
      error={error}
      onClickPasswordReset={handleClickPasswordReset}
      title={title}
      accounts={accounts}
      currentAccount={currentAccount}
      onLogin={handleLogin}
    />
  );
};
